import { computed, unref, type Ref } from 'vue';
import type { Applicant, ApplicantId, LoanApplication } from '@/types';
import { ApplicantType } from '@/helpers/const';
import { getFullName } from '@/helpers/format';

export function useHouseholdApplicants(
  loanApp: Ref<LoanApplication>,
  authorisedUser: Ref<Record<string, string>> | Record<string, string>,
  flags: { isSecondaryDeclaration?: boolean; isIncomeExpenseRequest?: boolean } = {},
) {
  const au = computed(() => unref(authorisedUser));
  const authorisedUserId = computed<ApplicantId | undefined>(() => au.value?.id as ApplicantId | undefined);

  const isApplicantMarriedToAnotherApplicant = (a: Applicant) =>
    a.relationshipStatus === 'married_or_defacto' &&
    a.relatedApplicantIndex !== undefined &&
    a.relatedApplicantIndex >= 0 &&
    a.relatedApplicantIndex < loanApp.value.applicants.length &&
    loanApp.value.applicants[a.relatedApplicantIndex] != null;
  const primaryApplicant = computed(
    () => loanApp.value.applicants.find((a) => a.type === ApplicantType.PRIMARY) ?? null,
  );

  const primaryPartner = computed(() => {
    const p = primaryApplicant.value;
    if (p && p.relatedApplicantIndex !== undefined && isApplicantMarriedToAnotherApplicant(p)) {
      const partner = loanApp.value.applicants[p.relatedApplicantIndex];
      return { id: partner.id as ApplicantId, name: getFullName(partner), type: partner.type };
    }
    return null;
  });

  const otherApplicants = computed(() =>
    loanApp.value.applicants
      .filter((a) => a.type !== ApplicantType.PRIMARY && a.id !== primaryPartner.value?.id)
      .map((a) => ({ id: a.id as ApplicantId, name: getFullName(a), type: a.type })),
  );

  const applicantsMarried = computed(() => loanApp.value.applicants.some(isApplicantMarriedToAnotherApplicant));

  const partnerOf = (a: Applicant | null | undefined): Applicant | null => {
    if (!a || a.relatedApplicantIndex == null) return null;
    if (!isApplicantMarriedToAnotherApplicant(a)) return null;
    return loanApp.value.applicants[a.relatedApplicantIndex] ?? null;
  };

  /**
   * Valid applicants = the current user, and their partner
   * Other applicants are not included
   * If the user is a Broker, they see all applicants
   */
  const validApplicants = computed<ApplicantId[]>(() => {
    const { applicants, userType } = loanApp.value;
    const ids = new Set<ApplicantId>();

    // For income/expense requests or secondary declarations,
    // restrict to current user + partner regardless of user type
    if (flags.isIncomeExpenseRequest || flags.isSecondaryDeclaration) {
      const me = applicants.find((a) => a.id === authorisedUserId.value) ?? null;
      if (me) {
        ids.add(me.id as ApplicantId);
        const p = partnerOf(me);
        if (p) ids.add(p.id as ApplicantId);
      }
    } else {
      // Determine base set of applicants based on user type
      if (userType === 'Broker') {
        applicants.forEach((a) => ids.add(a.id as ApplicantId));
      } else if (userType === 'Primary') {
        const primary = primaryApplicant.value;
        if (primary) {
          ids.add(primary.id as ApplicantId);
          const p = partnerOf(primary);
          if (p) ids.add(p.id as ApplicantId);
        }
      } else if (userType === 'Secondary') {
        const me = applicants.find((a) => a.id === authorisedUserId.value) ?? null;
        if (me) {
          ids.add(me.id as ApplicantId);
          const p = partnerOf(me);
          if (p) ids.add(p.id as ApplicantId);
        }
      }
    }

    // Ensure authorised user is always included
    if (authorisedUserId.value && applicants.some((a) => a.id === authorisedUserId.value)) {
      ids.add(authorisedUserId.value as ApplicantId);
    }

    return Array.from(ids);
  });

  /**
   * Household valid applicants = one applicant per household (for expenses step)
   * For married couples, only the first person in the pair is returned
   * For single applicants, they are returned as-is
   */
  const householdValidApplicants = computed<ApplicantId[]>(() => {
    if (!applicantsMarried.value) {
      return validApplicants.value;
    }

    const { applicants, userType } = loanApp.value;

    // For non-Broker: validApplicants already filtered to current user + partner
    // Just show the first one as the household head
    if (userType !== 'Broker') {
      return validApplicants.value.length > 0 ? [validApplicants.value[0]] : [];
    }

    // For Broker: group all applicants by married pairs
    const processed = new Set<ApplicantId>();
    const householdHeads: ApplicantId[] = [];

    applicants.forEach((applicant) => {
      if (processed.has(applicant.id as ApplicantId)) return;

      const partner = partnerOf(applicant);

      if (partner) {
        // This person is the household head for this married couple
        householdHeads.push(applicant.id as ApplicantId);
        processed.add(applicant.id as ApplicantId);
        processed.add(partner.id as ApplicantId);
      } else {
        // Single applicant
        householdHeads.push(applicant.id as ApplicantId);
        processed.add(applicant.id as ApplicantId);
      }
    });

    return householdHeads;
  });

  return {
    isApplicantMarriedToAnotherApplicant,
    primaryApplicant,
    primaryPartner,
    otherApplicants,
    applicantsMarried,
    validApplicants,
    householdValidApplicants,
    authorisedUserId,
  };
}
